import type { TrackingIntegration } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { ApiError } from '../../../../../shared/shared.api';
import { apiErrorReason } from '../../../policies/api-error-reason.policy';
import { disableIntegration, enableIntegration, getIntegration, uninstallIntegration } from '../gps.api';

export type GpsIntegrationState =
  { status: 'loading' } | { status: 'loaded'; integration: TrackingIntegration } | { status: 'not_found' } | { status: 'error' };

/** One GPS integration's page: the installation, and turning it on, off or uninstalling it. A failure toasts with the API's reason (a failed connection test on enable). */
export function useGpsIntegration(id: string) {
  const toast = useToast();
  const [state, setState] = useState<GpsIntegrationState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    getIntegration(id)
      .then((integration) => !cancelled && setState({ status: 'loaded', integration }))
      .catch((err) => !cancelled && setState({ status: err instanceof ApiError && err.status === 404 ? 'not_found' : 'error' }));
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function act(action: () => Promise<TrackingIntegration>, success: string, failure: string): Promise<void> {
    try {
      setState({ status: 'loaded', integration: await action() });
      toast.success(success);
    } catch (err) {
      toast.error(apiErrorReason(err, failure));
    }
  }

  return {
    state,
    /** After a settings save, the page shows what was saved. */
    replace: (integration: TrackingIntegration) => setState({ status: 'loaded', integration }),
    enable: (item: TrackingIntegration) => act(() => enableIntegration(item.id), `${item.displayName} enabled`, `Couldn't enable ${item.displayName}`),
    disable: (item: TrackingIntegration) => act(() => disableIntegration(item.id), `${item.displayName} disabled`, `Couldn't disable ${item.displayName}`),
    uninstall: (item: TrackingIntegration) =>
      act(() => uninstallIntegration(item.id), `${item.displayName} uninstalled`, `Couldn't uninstall ${item.displayName}`),
  };
}
